/**
 * Authenticated fetch helpers for the trc-api admin routes. Attaches the
 * session JWT as a Bearer header. A 401 clears the session (revoked access
 * or expired token) so the router bounces to /login; a 403 means "your role
 * can't do this" — the session is fine, so the page shows the error instead
 * of an editor getting force-signed-out by clicking an admin-only page.
 */

import { getSession, signOut } from './auth';

export const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.therivercrew.net';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = getSession();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (session) headers.Authorization = `Bearer ${session.access_token}`;
  if (init.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401) {
    signOut();
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? 'Session expired — please sign in again.');
  }
  if (res.status === 403) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(403, body?.error ?? 'Your role does not have access to this.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

/* ─── Row types (mirror the API JSON shapes) ─────────────────────── */

export interface TaxonomyRef {
  slug: string;
  name: string;
}

export interface Trip {
  id: string;
  slug: string;
  name: string;
  species_id: string | null;
  river_id: string | null;
  trip_type_id: string | null;
  fareharbor_flow_id: string | null;
  fareharbor_item_pk: string | null;
  description_md: string | null;
  included_md: string | null;
  meeting_point: string | null;
  price_adult_cents: number | null;
  price_child_cents: number | null;
  capacity: number;
  duration_minutes: number | null;
  season_start_month: number | null;
  season_end_month: number | null;
  hero_image_url: string | null;
  gallery_image_urls: string[];
  status: 'draft' | 'active' | 'archived' | 'paused';
  meta_title: string | null;
  meta_description: string | null;
  created_at: string;
  updated_at: string;
  species: TaxonomyRef | null;
  river: TaxonomyRef | null;
  trip_type: TaxonomyRef | null;
}

export interface TaxonomyRow {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
}

export interface BusinessInfo {
  id: number;
  business_name: string;
  tagline: string | null;
  phone: string | null;
  email: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_country: string | null;
  latitude: number | null;
  longitude: number | null;
  facebook: string | null;
  instagram: string | null;
  tripadvisor: string | null;
  google_business_url: string | null;
  uscg_license_number: string | null;
  adfg_permit_number: string | null;
  updated_at: string;
}
